import { EstimateStatus, ProjectStatus } from '../types';
import type { Traducir } from '../i18n/idioma';
import type { Clave } from '../i18n/textos';
import type { Estimate, Project, ContractInvoice } from '../types';
import { perVisitRevenue, billedPeriods, visitIsBilled } from './serviceRevenue';

/**
 * Revenue Trends data layer.
 *
 * Revenue here means MONEY COLLECTED, not money booked. A dollar lands on the
 * chart on the day it was actually received, so a $50k job that started in
 * March but was paid in June shows up in June.
 *
 * Sources, in priority order (each job is counted exactly once):
 *   1. Paid project invoices  — most precise: real amount, real date.
 *   2. Estimates marked Paid  — the canonical persisted "this job was paid"
 *                               signal (`paidAt`). Skipped when the project it
 *                               converted into already reported paid invoices.
 *   3. Paid contract invoices — the billed record of a service month.
 *   4. Trabajo de ruta sin facturar — sólo los meses que ninguna factura cubre,
 *                                 valorados en la unidad del contrato: la visita
 *                                 si se cobra por visita, el mes entero si es
 *                                 mensual (utils/serviceRevenue.ts).
 *
 * Cancelled projects never contribute.
 */

export type RevenueSource = 'invoice' | 'estimate' | 'service';

export interface RevenueEvent {
  date: Date;
  amount: number;
  source: RevenueSource;
  label: string;
}

export interface WeekBucket {
  /** Short axis label, e.g. "Week 1". */
  name: string;
  /** Human date span, e.g. "Aug 1 – 7". */
  range: string;
  /** Collected revenue in this bucket. */
  value: number;
  firstDay: number;
  lastDay: number;
}

export interface RevenueSources {
  projects: Project[];
  estimates: Estimate[];
  serviceVisits: any[];
  serviceContracts: any[];
  /** Monthly billing rows. Optional: a workspace that has never generated an
   *  invoice — and every month recorded before billing existed — falls back to
   *  valuing visits, exactly as this engine did before. */
  contractInvoices?: ContractInvoice[];
}

/**
 * Los meses como CLAVES, no como texto.
 *
 * Estaban escritos aquí en inglés y salían en el panel —"Jan Feb Mar"— aunque
 * la app estuviera en español, y peor: metidos dentro de una frase traducida,
 * "cobrado en Aug". Este archivo es un util, no un componente, así que quien
 * los pinta es el que traduce.
 */
export const MESES: Clave[] = [
  'mes.1', 'mes.2', 'mes.3', 'mes.4', 'mes.5', 'mes.6',
  'mes.7', 'mes.8', 'mes.9', 'mes.10', 'mes.11', 'mes.12',
];

export const MONTH_NAMES = MESES;

/**
 * Parse a stored date string without the UTC-shift bug.
 *
 * `new Date('2026-08-03')` is parsed as UTC midnight, which in any negative
 * offset (all of the US) is the *previous* day locally — enough to push revenue
 * into the wrong week. Date-only strings are therefore built as local midnight;
 * full ISO timestamps already carry an offset and parse correctly.
 */
export function parseLocalDate(value?: string | null): Date | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Collect every "money received" event across all three revenue sources. */
export function collectRevenueEvents({
  projects,
  estimates,
  serviceVisits,
  serviceContracts,
  contractInvoices = [],
}: RevenueSources): RevenueEvent[] {
  const events: RevenueEvent[] = [];

  // 1. Paid invoices on live projects. Cancelled work never counts.
  //    `paidAt` is preferred but optional on historical rows, so the invoice
  //    issue date is the fallback.
  const invoicedByProject = new Map<string, number>();
  projects
    .filter(p => p.status !== ProjectStatus.CANCELLED)
    .forEach(p => {
      (p.invoices || [])
        .filter(inv => inv.status === 'Paid')
        .forEach(inv => {
          const date = parseLocalDate(inv.paidAt) || parseLocalDate(inv.date);
          if (!date) return;
          const amount = inv.amount || 0;
          events.push({
            date,
            amount,
            source: 'invoice',
            label: `${p.name} — invoice ${inv.id}`,
          });
          invoicedByProject.set(p.id, (invoicedByProject.get(p.id) || 0) + amount);
        });
    });

  // 2. Estimates marked Paid — counted for whatever the invoices haven't already
  //    accounted for.
  //
  //    A paid invoice on a job whose proposal is also marked Paid is a record of
  //    PART of the same money, not extra money. Previously any invoice caused the
  //    whole proposal to be dropped, so adding a small invoice to a fully-paid
  //    job made revenue collapse to the invoice amount. Now the invoices count in
  //    full and the proposal tops up the remainder, so a job's collected revenue
  //    can only ever go up — while the same dollar is still never counted twice.
  //    Invoices exceeding the proposal (change orders) simply stand on their own.
  //
  //    LO QUE ESTA RESTA SUPONE, y no puede comprobar, está medido en la sección
  //    «Anticipo o trabajo extra» de más abajo: si la factura cobra trabajo
  //    adicional, restarla se come dinero real. `bandaDeCobro` publica el otro
  //    extremo en vez de esconderlo. Aquí no se cambia nada: el gráfico necesita
  //    UNA cifra por día y ésta es la que no puede contar dos veces el mismo
  //    dólar. El techo se enseña al lado, etiquetado, no dentro del total.
  estimates
    .filter(e => e.status === EstimateStatus.PAID)
    .forEach(e => {
      const alreadyInvoiced = e.convertedToProjectId
        ? invoicedByProject.get(e.convertedToProjectId) || 0
        : 0;
      const remainder = (e.total || 0) - alreadyInvoiced;
      if (remainder <= 0) return; // invoices already cover the whole proposal
      const date =
        parseLocalDate(e.paidAt) ||
        parseLocalDate(e.signedDate) ||
        parseLocalDate(e.dateCreated);
      if (!date) return;
      events.push({
        date,
        amount: remainder,
        source: 'estimate',
        label: `${e.clientName} — proposal paid`,
      });
    });

  // 3. Paid contract invoices — for a billed month this is the real amount on
  //    the real payment date, the same standard applied to a project invoice.
  //    An unpaid one contributes nothing: it is money owed, not money received.
  const contractById = new Map(serviceContracts.map(c => [c.id, c]));
  contractInvoices
    .filter(i => i.status === 'Paid')
    .forEach(i => {
      // `issuedOn` is the fallback for rows paid before paidAt was stamped —
      // it still dates to the month billed, which is close enough to be useful
      // and never invents a date out of thin air.
      const date = parseLocalDate(i.paidAt) || parseLocalDate(i.issuedOn);
      if (!date) return;
      events.push({
        date,
        amount: i.amount || 0,
        source: 'service',
        label: `${contractById.get(i.contractId)?.clientName || 'Service contract'} — ${i.period}`,
      });
    });

  // 4. Completed visits in months NO invoice covers, valued at the contract's
  //    per-visit rate. This is the estimate that keeps pre-billing history and
  //    un-invoiced workspaces intact; where an invoice exists it has already
  //    spoken for the month, and counting the visits too would double-count.
  //
  //    ─── POR QUÉ ESTO SIGUE VALORANDO POR VISITA Y NO POR MES ───
  //
  //    Sabemos que valorar por visita SUBESTIMA el trabajo de ruta sin facturar:
  //    un contrato mensual se cobra entero aunque el mes traiga menos visitas de
  //    las 4,33 que asume el divisor, y `unbilledEarnings` (utils/serviceRevenue.ts)
  //    ya calcula la cifra buena — la misma con la que Rutas ▸ Cobros emite la
  //    factura que recibe el cliente. En toda la base la diferencia es 3,01x.
  //
  //    Y aun así ESTE motor se queda con la valoración por visita, a propósito.
  //    `collectRevenueEvents` no es de Finanzas: lo leen también el gráfico de
  //    ingresos del Panel, el asistente de IA (services/aiChat.ts), la agenda de
  //    seguimientos (utils/followUpSchedule.ts) y utils/completedProjectVisibility.ts.
  //    Cambiar aquí la fórmula haría que a cuatro pantallas que nadie ha tocado
  //    les subiera el ingreso 3x de un día para otro, sin explicación. Una cifra
  //    que sube sola destruye la confianza igual que una que baja sola.
  //
  //    La cifra honesta NO se pierde: Finanzas la lee por su cuenta con
  //    `unbilledEarnings` y la enseña como «por facturar», etiquetada y aparte,
  //    que es información nueva en vez de un número que cambia a escondidas.
  //    Subir esta valoración al motor compartido es una decisión de producto
  //    (parada P-11 del plan): que la tome una persona y se anuncie.
  const billed = billedPeriods(contractInvoices);
  serviceVisits
    .filter(v => v.status === 'Completed')
    .forEach(v => {
      const contract = contractById.get(v.contractId);
      const date = parseLocalDate(v.date);
      if (!contract || !date) return;
      if (visitIsBilled(v, billed)) return;
      events.push({
        date,
        amount: perVisitRevenue(contract),
        source: 'service',
        label: 'Service visit',
      });
    });

  return events;
}

// ── Anticipo o trabajo extra: el dato que no existe ──────────────
//
// El paso 2 de ahí arriba resta del presupuesto pagado lo que ya facturó su
// obra. Eso SUPONE que toda factura cobra una parte de lo ya presupuestado —un
// anticipo, un avance de obra— y por eso la descuenta. Cuando la factura cobra
// trabajo ADICIONAL, la suposición se come dinero que sí entró.
//
// Medido contra producción el 11-ago-2026: hay exactamente DOS facturas en toda
// la base, y las dos huelen a trabajo extra. La de la obra 024244ac se llama
// 'RAMA', vale $365, su única partida dice «GRADING EXTRA» y se emitió a las
// 01:12:47 del 6-ago — QUINCE MINUTOS después de que su propuesta se marcara
// Pagada, a las 00:57:25. La otra ('24124 ', $790) son dos partidas «GRADING».
// Ninguna de esas descripciones aparece en las partidas de su presupuesto. Si
// las dos son trabajo extra, el motor publica $5.901,49 donde el cliente entregó
// $6.266,49.
//
// POR QUÉ NO SE DECIDE AQUÍ, Y NO ES PEREZA: no hay ni un campo que lo diga.
// `ProjectInvoice` guarda folio, fechas, importe, estado y partidas de texto
// libre tecleadas a mano (components/project/ProjectFinancials.tsx:269-278); no
// existe el concepto de anticipo ni una referencia a la partida del presupuesto
// que se está cobrando. Se probaron los tres criterios derivables del dato que
// hay, y los tres se caen:
//
//   - POR FECHA — «una factura posterior al cobro de la propuesta no puede ser
//     un anticipo de ella». Acierta con RAMA (15 minutos después), pero rompe
//     una prueba que YA EXISTÍA: revenueTrends.test.ts:404 tiene dos anticipos
//     legítimos y uno está fechado el día siguiente al `paidAt` de la propuesta.
//   - POR IMPORTE — «lo que excede al contrato es extra». Ya está en el motor y
//     no cambia ninguna cifra: propuesta + exceso da exactamente la suma de las
//     facturas, que es lo que ya se publica. Y con RAMA ($365 sobre $5.901) no
//     hay exceso ninguno.
//   - POR PARTIDAS — «si la factura detalla partidas, cobra otra cosa». Toda
//     factura creada por el formulario trae partidas: clasificaría TODAS como
//     trabajo extra y duplicaría cualquier anticipo de verdad.
//
// Lo que sí se puede hacer sin inventarse el dato es DEJAR DE ELEGIR EN
// SILENCIO. El motor publica un número; aquí salen los dos que el dato admite y,
// en medio, el importe exacto que está en disputa. Es el mismo remedio que
// projectCostBasis aplica al material cotizado (utils/financeSummary.ts:180-194)
// y por el mismo motivo: tres lecturas defendibles, ninguna mezclada a ciegas.
//
// PARADA P-7: cerrarlo de verdad pide un campo en `ProjectInvoice` —del estilo
// `kind?: 'contract' | 'extra'`, o el `changeOrderId` que ate la factura a la
// orden de cambio que la justifica— y types.ts está cerrado en esta ola.

/** Las dos lecturas posibles del dinero cobrado de una obra, y la distancia
 *  entre ellas. Ninguna es «la buena»: eso es justo lo que no se sabe. */
export interface BandaDeCobro {
  /** Total de la propuesta marcada 'Paid'. 0 si no lo está, o si no hay. */
  propuestaPagada: number;
  /** Suma de las facturas 'Paid' de la obra. Las 'Sent'/'Overdue' son dinero
   *  debido, no recibido, y no entran. */
  facturasPagadas: number;
  /** Suponiendo que TODA factura cobra parte de lo ya presupuestado. Es el
   *  número que el motor publica hoy y el mínimo que consta cobrado: nunca baja
   *  de la propuesta pagada ni de las facturas pagadas, así que una propuesta
   *  pagada no puede desaparecer del ingreso por añadirle una factura. */
  piso: number;
  /** Suponiendo que NINGUNA lo hace: los dos papeles, sumados. Es el máximo que
   *  el dato admite, no una estimación optimista. */
  techo: number;
  /** techo − piso: los dólares que el motor DA POR SUPUESTO que son los mismos
   *  dólares contados dos veces. Es el tamaño exacto de lo que no se sabe, y lo
   *  que hay que enseñar al lado de la cifra en vez de un número a secas. */
  solapeSupuesto: number;
  /** true cuando piso y techo difieren, es decir cuando la obra tiene propuesta
   *  pagada Y facturas pagadas a la vez. Con una sola de las dos no hay nada
   *  que suponer y la cifra es firme. */
  ambiguo: boolean;
}

/** La banda a partir de los dos totales. Función pura: la misma aritmética la
 *  usan el motor del panel y el de Finanzas, para que no puedan discrepar. */
export function bandaDeCobro(propuestaPagada: number, facturasPagadas: number): BandaDeCobro {
  // El piso se calcula con la MISMA expresión que ya usaban los dos motores
  // —facturas enteras, más lo que la propuesta añade encima— y no con un
  // Math.max equivalente: sobre flotantes las dos formas pueden diferir en el
  // último bit, y este campo tiene que salir idéntico al `collected` publicado.
  const piso = facturasPagadas + Math.max(0, propuestaPagada - facturasPagadas);
  const techo = propuestaPagada + facturasPagadas;
  const solapeSupuesto = techo - piso;
  return {
    propuestaPagada,
    facturasPagadas,
    piso,
    techo,
    solapeSupuesto,
    ambiguo: solapeSupuesto > 0,
  };
}

export interface CobroDeObra extends BandaDeCobro {
  projectId: string;
  projectName: string;
}

/**
 * La banda de cada obra viva.
 *
 * Se emparejan igual que en collectRevenueEvents —la propuesta busca su obra por
 * `convertedToProjectId`— para que el `piso` de aquí sea exactamente el dinero
 * que aquel motor publica para esa obra, y no una segunda cuenta parecida.
 *
 * Las obras canceladas quedan fuera, como en todo este archivo. Una propuesta
 * pagada sin obra a la que apuntar no puede solaparse con ninguna factura, así
 * que no genera banda: su cifra ya es firme.
 *
 * OJO AL EMPAREJAR: aquí la propuesta encuentra su obra por
 * `estimate.convertedToProjectId`, y en projectFinancials es la obra la que
 * encuentra su propuesta por `project.estimateId`. Son dos punteros distintos
 * que hoy están puestos los dos en las 59 filas de producción, pero nada los
 * obliga a coincidir: si un día uno se pierde, esta banda y la de Finanzas
 * discreparán para esa obra. No se unifican aquí porque cada una tiene que
 * salir idéntica al motor al que acompaña.
 */
export function cobrosPorObra(projects: Project[], estimates: Estimate[]): CobroDeObra[] {
  const propuestaPorObra = new Map<string, number>();
  estimates
    .filter(e => e.status === EstimateStatus.PAID)
    .forEach(e => {
      if (!e.convertedToProjectId) return;
      // Se suma en vez de asignar: nada impide hoy que dos propuestas pagadas
      // apunten a la misma obra, y quedarse con la última perdería una entera.
      propuestaPorObra.set(
        e.convertedToProjectId,
        (propuestaPorObra.get(e.convertedToProjectId) || 0) + (e.total || 0),
      );
    });

  return projects
    .filter(p => p.status !== ProjectStatus.CANCELLED)
    .map(p => {
      const facturasPagadas = (p.invoices || [])
        .filter(inv => inv.status === 'Paid')
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);
      return {
        projectId: p.id,
        projectName: p.name,
        ...bandaDeCobro(propuestaPorObra.get(p.id) || 0, facturasPagadas),
      };
    });
}

export interface TotalesDeCobro {
  piso: number;
  techo: number;
  solapeSupuesto: number;
  /** Cuántas obras arrastran la duda. Sin esto, «$1.155 en disputa» no
   *  distingue una obra rara de un problema de método. */
  obrasAmbiguas: number;
}

/** Suma las bandas. `techo` NO es `piso` inflado un tanto por ciento: es la otra
 *  lectura del mismo dato, sumada obra a obra. */
export function totalesDeCobro(cobros: CobroDeObra[]): TotalesDeCobro {
  return cobros.reduce<TotalesDeCobro>(
    (acc, c) => {
      acc.piso += c.piso;
      acc.techo += c.techo;
      acc.solapeSupuesto += c.solapeSupuesto;
      if (c.ambiguo) acc.obrasAmbiguas += 1;
      return acc;
    },
    { piso: 0, techo: 0, solapeSupuesto: 0, obrasAmbiguas: 0 },
  );
}

/**
 * Split a calendar month into exactly four weeks.
 *
 * Weeks 1–3 are 7 days each; week 4 absorbs day 22 through the end of the month
 * (7–10 days depending on month length). Always four bars, and the four always
 * sum to the month total — no stubby 1–3 day final bar that reads as a crash.
 */
export function weekRangesForMonth(year: number, month: number): Array<{ firstDay: number; lastDay: number }> {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return [
    { firstDay: 1, lastDay: 7 },
    { firstDay: 8, lastDay: 14 },
    { firstDay: 15, lastDay: 21 },
    { firstDay: 22, lastDay: daysInMonth },
  ];
}

/** Which of the four weeks a given day-of-month falls into (0-based). */
export function weekIndexForDay(day: number): number {
  return Math.min(3, Math.floor((day - 1) / 7));
}

/** Bucket a month's collected revenue into the four week bars. */
export function bucketMonthIntoWeeks(
  year: number,
  month: number,
  events: RevenueEvent[],
  /** Suelta, no componente: `t` entra por parámetro. */
  t?: Traducir,
): WeekBucket[] {
  const buckets: WeekBucket[] = weekRangesForMonth(year, month).map((range, i) => ({
    // `semana` y `mes` son datos; el texto lo arma la vista, que sí tiene idioma.
    name: t ? t('panel.ingresos.semana', { n: i + 1 }) : `Week ${i + 1}`,
    range: `${t ? t(MESES[month]) : ''} ${range.firstDay} – ${range.lastDay}`.trim(),
    value: 0,
    firstDay: range.firstDay,
    lastDay: range.lastDay,
  }));

  events.forEach(({ date, amount }) => {
    if (date.getFullYear() !== year || date.getMonth() !== month) return;
    buckets[weekIndexForDay(date.getDate())].value += amount;
  });

  return buckets;
}

/** Total collected revenue for one calendar month. */
export function monthTotal(year: number, month: number, events: RevenueEvent[]): number {
  return events.reduce(
    (sum, e) =>
      e.date.getFullYear() === year && e.date.getMonth() === month ? sum + e.amount : sum,
    0,
  );
}

/** Total collected revenue for a whole year. */
export function yearTotal(year: number, events: RevenueEvent[]): number {
  return events.reduce((sum, e) => (e.date.getFullYear() === year ? sum + e.amount : sum), 0);
}

/** The calendar month immediately before the given one. */
export function previousMonth(year: number, month: number): { year: number; month: number } {
  return month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 };
}

/**
 * Percent change from the previous month, or null when there's no prior revenue
 * to compare against (avoids a meaningless "+∞%" on the first month of trading).
 */
export function monthOverMonthChange(
  year: number,
  month: number,
  events: RevenueEvent[],
): number | null {
  const prev = previousMonth(year, month);
  const prevTotal = monthTotal(prev.year, prev.month, events);
  if (prevTotal <= 0) return null;
  return ((monthTotal(year, month, events) - prevTotal) / prevTotal) * 100;
}

/**
 * Years that should appear in the year picker: every year with revenue, plus
 * the current year, so a brand-new workspace still has something to select.
 */
export function availableYears(events: RevenueEvent[], today: Date = new Date()): number[] {
  const years = new Set<number>([today.getFullYear()]);
  events.forEach(e => years.add(e.date.getFullYear()));
  return [...years].sort((a, b) => b - a);
}

/** Months of a year that actually have revenue — used to dim empty month tabs. */
export function monthsWithRevenue(year: number, events: RevenueEvent[]): Set<number> {
  const months = new Set<number>();
  events.forEach(e => {
    if (e.date.getFullYear() === year && e.amount > 0) months.add(e.date.getMonth());
  });
  return months;
}
